/**
 * Global authentication context for the Venture Readiness Portal.
 *
 * Wraps the app in <AuthProvider> to make the current user available
 * anywhere via useAuth().  On mount, AuthProvider calls /auth/me to restore
 * the session from the existing HttpOnly cookie — if the cookie is absent or
 * expired, user is set to null and the app redirects to /login.
 *
 * Context value:
 *   user          — the authenticated User object, or null if not logged in
 *   loading       — true while the initial /me call is in flight (prevents flash-of-login)
 *   login(email, password) — calls the API and sets user on success
 *   logout()      — calls the API to clear the cookie, then sets user to null
 *   avatarVersion — integer bumped by bumpAvatar() to force avatar cache-bust
 *   bumpAvatar()  — call after uploading a new avatar so the navbar reloads it
 */
import React, { createContext, useContext, useState, useEffect } from 'react';
import { login as apiLogin, logout as apiLogout, getMe, mfaLogin as apiMfaLogin } from '../services/apiService';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Attempt to restore an existing session from the HttpOnly cookie.
  // getMe() returns null (or throws) if there is no valid session.
  useEffect(() => {
    getMe()
      .then((u) => setUser(u))
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);

  async function login(email, password) {
    const result = await apiLogin(email, password);
    // MFA required — return the challenge without setting user
    if (result?.mfa_required) return result;
    setUser(result);
    return result;
  }

  async function mfaLogin(mfa_token, code) {
    const u = await apiMfaLogin(mfa_token, code);
    setUser(u);
    return u;
  }

  async function logout() {
    await apiLogout();
    setUser(null);
  }

  // avatarVersion is an integer incremented whenever the user uploads a new
  // avatar.  The Navbar passes it as a query param to the avatar <img> src,
  // which forces the browser to re-fetch the image instead of serving the cache.
  const [avatarVersion, setAvatarVersion] = useState(1);
  function bumpAvatar() { setAvatarVersion((v) => v + 1); }

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, mfaLogin, logout, avatarVersion, bumpAvatar }}>
      {children}
    </AuthContext.Provider>
  );
}

/** Hook to access auth state and actions from any component. */
export function useAuth() {
  return useContext(AuthContext);
}
